/**
 * Compliance Mixin for Alpine.js Slideover Components
 *
 * Extracts all compliance/document-upload functionality so it can be shared
 * between the Person slideover and the Application slideover.
 *
 * Requirements on the consuming component:
 *   - `this.person`   — object with `tenant_checks`, `tenant_check_summary`,
 *                        `current_tenancy`, `current_tenancy_applicant`
 *   - `this.personId` — the person's ID (used for API endpoints)
 *   - `this.fetchPerson()` — refreshes `this.person` from the server
 */

export function complianceMixin() {
  return {
    // Generic document upload state - keyed by check type
    docUpload: {},

    // Image lightbox state
    lightboxOpen: false,
    lightboxImageUrl: null,

    // Override modal state (for accepting documents with mismatches)
    overrideModal: {
      open: false,
      checkType: null,
      reason: '',
      mismatchType: null,
      saving: false,
      error: null
    },

    // Referencing approval modal state
    referencingModal: {
      open: false,
      applicationId: null,
      propertyName: null,
      saving: false,
      error: null
    },

    // ========== State Helpers ==========

    getDocUploadState(checkType) {
      if (!this.docUpload[checkType]) {
        this.docUpload[checkType] = this.createDocUploadState(checkType)
      }
      return this.docUpload[checkType]
    },

    createDocUploadState(checkType) {
      const defaults = {
        open: false,
        state: 'idle',
        progress: 0,
        analysisResult: null,
        blobSignedId: null,
        imageUrl: null,
        thumbnailUrl: null,
        pdfUrl: null,
        isPdf: false,
        error: null,
        showRejectInput: false,
        rejectReason: '',
        documentType: null
      }

      if (checkType === 'address_verification') {
        defaults.documentType = 'utility_bill'
      }

      // Right to Rent has a second route for somebody with no passport: two
      // List A Group 2 documents seen together. The form lives here so it
      // survives the zone re-rendering as slots fill.
      if (checkType === 'right_to_rent') {
        defaults.mode = 'share_code'
        defaults.rtrDocs = [ this.blankRtrDoc(), this.blankRtrDoc() ]
        defaults.rtrCheckDate = new Date().toISOString().slice(0, 10)
        defaults.saving = false
      }

      return defaults
    },

    // No default type: Claude reads the document and proposes one, and the
    // agent can change it. `state` is idle → analyzing → analyzed | error.
    blankRtrDoc() {
      return { type: '', file: null, previewUrl: null, date: '', state: 'idle', blobSignedId: null, analysis: null, error: null }
    },

    resetDocUpload(checkType) {
      this.docUpload[checkType] = this.createDocUploadState(checkType)
      const fileInput = document.getElementById(`${checkType}-file-input-${this.personId}`)
      if (fileInput) {
        fileInput.value = ''
      }
    },

    resetAllDocUploads() {
      this.docUpload = {}
      this.lightboxOpen = false
      this.lightboxImageUrl = null
    },

    // ========== Check Type Helpers ==========

    checkTypeUsesAI(checkType) {
      return checkType === 'id_verification' || checkType === 'address_verification' || checkType === 'right_to_rent'
    },

    checkTypeNeedsDocTypeSelection(checkType) {
      return ['sanctions', 'referencing'].includes(checkType)
    },

    getDocumentTypeOptions(checkType) {
      const options = {
        id_verification: [
          { value: 'passport', label: 'Passport' },
          { value: 'driving_licence', label: 'Driving Licence' },
          { value: 'national_id', label: 'National ID Card' },
          { value: 'brp', label: 'Biometric Residence Permit' },
          { value: 'residence_permit', label: 'Residence Permit' }
        ],
        address_verification: [
          { value: 'utility_bill', label: 'Utility Bill' },
          { value: 'bank_statement', label: 'Bank Statement' },
          { value: 'council_tax', label: 'Council Tax' },
          { value: 'hmrc_letter', label: 'HMRC Letter' }
        ],
        right_to_rent: [
          { value: 'share_code', label: 'Share Code Check' },
          { value: 'passport', label: 'UK/Irish Passport' },
          { value: 'brp', label: 'Biometric Residence Permit' },
          { value: 'euss', label: 'EU Settlement Scheme' },
          { value: 'visa', label: 'Visa' },
          { value: 'other', label: 'Other' }
        ],
        sanctions: [
          { value: 'sanctions_check', label: 'Sanctions Check Report' },
          { value: 'pep_check', label: 'PEP Check Report' },
          { value: 'combined_check', label: 'Combined Sanctions/PEP Check' },
          { value: 'other', label: 'Other' }
        ],
        referencing: [
          { value: 'full_reference', label: 'Full Reference Report' },
          { value: 'landlord_reference', label: 'Landlord Reference' },
          { value: 'employer_reference', label: 'Employer Reference' },
          { value: 'credit_check', label: 'Credit Check' },
          { value: 'affordability_check', label: 'Affordability Check' },
          { value: 'other', label: 'Other' }
        ]
      }
      return options[checkType] || []
    },

    getCheckTypeDisplayName(checkType) {
      const names = {
        'id_verification': 'ID Verification',
        'address_verification': 'Address Verification',
        'right_to_rent': 'Right to Rent',
        'sanctions': 'Sanctions',
        'referencing': 'Referencing'
      }
      return names[checkType] || checkType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    },

    getCheckTypeIcon(checkType) {
      const icons = {
        'id_verification': 'ph-identification-card',
        'address_verification': 'ph-house-line',
        'right_to_rent': 'ph-shield-check',
        'sanctions': 'ph-warning-octagon',
        'referencing': 'ph-user-check'
      }
      return icons[checkType] || 'ph-file-doc'
    },

    formatDocumentType(type) {
      const types = {
        'passport': 'Passport',
        'driving_licence': 'Driving Licence',
        'national_id': 'National ID Card',
        'brp': 'Biometric Residence Permit',
        'residence_permit': 'Residence Permit',
        'other': 'Other Document',
        'utility_bill': 'Utility Bill',
        'bank_statement': 'Bank Statement',
        'council_tax': 'Council Tax',
        'hmrc_letter': 'HMRC Letter',
        'share_code': 'Share Code Check',
        'euss': 'EU Settlement Scheme'
      }
      return types[type] || type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || type
    },

    formatConfidence(confidence) {
      if (confidence == null) return '—'
      return Math.round(confidence * 100) + '%'
    },

    getQualityStatusColor(checkType) {
      const upload = this.getDocUploadState(checkType)
      if (!upload.analysisResult?.quality) return 'gray'
      return upload.analysisResult.quality.readable ? 'green' : 'orange'
    },

    getQualityStatusText(checkType) {
      const upload = this.getDocUploadState(checkType)
      if (!upload.analysisResult?.quality) return 'Unknown'
      return upload.analysisResult.quality.readable ? 'Good' : 'Issues Detected'
    },

    // ========== Summary Helpers ==========

    // Count accepted checks, including RTR inferred from passport
    effectiveAcceptedCount() {
      const dbCount = this.person?.tenant_check_summary?.accepted || 0
      // If RTR is satisfied by passport but no explicit RTR check is accepted, add 1
      if (this.isRtrSatisfiedByPassport()) {
        const rtrCheck = this.person?.tenant_checks?.find(c => c.check_type === 'right_to_rent')
        if (!rtrCheck || rtrCheck.status !== 'accepted') {
          return dbCount + 1
        }
      }
      return dbCount
    },

    // How many of *these* checks are done.
    //
    // Counted over the list on screen rather than off the person's summary,
    // because which checks are owed depends on why the person is here. A
    // guarantor is never asked for right to rent — they aren't going to live
    // there — so counting it, in either the numerator or the denominator,
    // would leave their compliance permanently one short.
    acceptedCountFor(checkTypes) {
      return (checkTypes || []).filter(checkType => {
        if (checkType === 'right_to_rent' && this.isRtrSatisfiedByPassport()) return true

        return this.person?.tenant_checks?.find(c => c.check_type === checkType)?.status === 'accepted'
      }).length
    },

    // ========== RTR Helpers ==========

    isRtrSatisfiedByPassport() {
      const rtrCheck = this.person?.tenant_checks?.find(c => c.check_type === 'right_to_rent')
      if (rtrCheck?.rtr_satisfied_by_passport) return true

      const idCheck = this.person?.tenant_checks?.find(c =>
        c.check_type === 'id_verification' &&
        ['accepted', 'awaiting_approval'].includes(c.status)
      )
      if (idCheck?.document_type !== 'passport') return false
      return ['GB', 'IE'].includes(idCheck?.issuing_country)
    },

    // Only collapse the R2R row to a single non-expandable line when the
    // R2R has no documents of its own (e.g. the legacy "ID-passport
    // auto-satisfies R2R" marker case). If the applicant uploaded their
    // own passport against the R2R check, fall through to the normal
    // expandable render so the agent can see the actual verifying doc.
    rtrShouldRenderCollapsed() {
      if (!this.isRtrSatisfiedByPassport()) return false
      const rtrCheck = this.person?.tenant_checks?.find(c => c.check_type === 'right_to_rent')
      return !(rtrCheck?.documents_data?.length > 0)
    },

    getRtrSatisfiedByPassportDisplay() {
      const rtrCheck = this.person?.tenant_checks?.find(c => c.check_type === 'right_to_rent')
      if (rtrCheck?.rtr_satisfied_by_passport) {
        return rtrCheck.rtr_basis_display
      }

      const idCheck = this.person?.tenant_checks?.find(c =>
        c.check_type === 'id_verification' &&
        ['accepted', 'awaiting_approval'].includes(c.status) &&
        c.document_type === 'passport'
      )
      if (idCheck?.issuing_country === 'GB') return 'British Passport'
      if (idCheck?.issuing_country === 'IE') return 'Irish Passport'
      return null
    },

    // ========== Toggle & File Handling ==========

    toggleDocUpload(checkType) {
      const upload = this.getDocUploadState(checkType)
      upload.open = !upload.open
      if (!upload.open) {
        this.resetDocUpload(checkType)
      }
    },

    handleDocFileSelect(event, checkType) {
      const file = event.target.files[0]
      if (!file) return
      this.uploadDocument(checkType, file)
    },

    handleDocFileDrop(event, checkType) {
      event.preventDefault()
      const file = event.dataTransfer.files[0]
      if (!file) return
      this.uploadDocument(checkType, file)
    },

    // ========== Upload & Verification ==========

    async uploadDocument(checkType, file) {
      const upload = this.getDocUploadState(checkType)

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf']
      if (!validTypes.includes(file.type)) {
        upload.error = 'Please upload an image (JPG, PNG, HEIC) or PDF file'
        upload.state = 'error'
        return
      }

      if (file.size > 10 * 1024 * 1024) {
        upload.error = 'File is too large. Maximum size is 10MB.'
        upload.state = 'error'
        return
      }

      upload.state = 'uploading'
      upload.progress = 0
      upload.error = null
      upload.isPdf = file.type === 'application/pdf'

      try {
        if (upload.isPdf && window.generatePdfThumbnail) {
          try {
            upload.thumbnailUrl = await window.generatePdfThumbnail(file, 200)
          } catch (e) {
            console.warn('Could not generate PDF thumbnail:', e)
          }
        }

        const formData = new FormData()
        formData.append('document', file)
        formData.append('check_type', checkType)

        const tenancy = this.person?.current_tenancy
        const applicant = this.person?.current_tenancy_applicant
        if (this.complianceGuaranteeId) {
          formData.append('guarantee_id', this.complianceGuaranteeId)
        } else if (tenancy) {
          formData.append('tenancy_id', tenancy.id)
        } else if (applicant) {
          formData.append('tenancy_application_applicant_id', applicant.id)
        }

        const endpoint = this.checkTypeUsesAI(checkType)
          ? `/people/${this.personId}/tenant-checks/analyze_document`
          : `/people/${this.personId}/tenant-checks/upload_document`

        if (this.checkTypeUsesAI(checkType)) {
          const progressInterval = setInterval(() => {
            if (upload.progress < 90) {
              upload.progress += 10
            }
          }, 200)

          upload.state = 'analyzing'
          upload.progress = 100

          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
          })

          clearInterval(progressInterval)
          const result = await response.json()

          if (result.success) {
            upload.analysisResult = result.analysis
            upload.blobSignedId = result.blob_signed_id
            upload.imageUrl = upload.thumbnailUrl || result.image_url
            upload.pdfUrl = upload.isPdf ? result.image_url : null
            upload.state = 'complete'
          } else {
            upload.error = result.error || 'Failed to analyze document'
            upload.state = 'error'
          }
        } else {
          if (upload.thumbnailUrl) {
            formData.append('thumbnail', upload.thumbnailUrl)
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
            }
          })

          const result = await response.json()

          if (result.success) {
            await this.fetchPerson()
            this.resetDocUpload(checkType)
            this.getDocUploadState(checkType).open = true
          } else {
            upload.error = result.error || 'Failed to upload document'
            upload.state = 'error'
          }
        }
      } catch (error) {
        console.error(`${checkType} upload error:`, error)
        upload.error = 'Upload failed. Please try again.'
        upload.state = 'error'
      }
    },

    // ========== Right to Rent from original documents ==========

    // List A Group 2. Any two together give an unlimited right to rent, so
    // this is the route for a British citizen with no passport.
    rtrGroup2Options() {
      return [
        { value: 'birth_certificate', label: 'Full birth certificate' },
        { value: 'government_letter', label: 'Benefits or government letter', dated: true },
        { value: 'driving_licence', label: 'UK driving licence' },
        { value: 'employer_letter', label: 'Employer letter', dated: true },
        { value: 'school_letter', label: 'College or university letter', dated: true },
        { value: 'dbs_certificate', label: 'DBS certificate', dated: true },
        { value: 'naturalisation_certificate', label: 'Naturalisation certificate' },
        { value: 'armed_forces_evidence', label: 'Armed forces evidence' }
      ]
    },

    rtrDocNeedsDate(type) {
      return !!this.rtrGroup2Options().find(o => o.value === type)?.dated
    },

    openRtrDocumentsForm() {
      const upload = this.getDocUploadState('right_to_rent')
      upload.mode = 'documents'
      upload.error = null
      upload.open = true
    },

    closeRtrDocumentsForm() {
      const upload = this.getDocUploadState('right_to_rent')
      upload.rtrDocs.forEach(d => { if (d.previewUrl) URL.revokeObjectURL(d.previewUrl) })
      upload.rtrDocs = [ this.blankRtrDoc(), this.blankRtrDoc() ]
      upload.mode = 'share_code'
      upload.error = null
    },

    async selectRtrDocFile(index, event) {
      const file = event.target.files[0]
      event.target.value = ''
      await this.takeRtrDocFile(index, file)
    },

    async dropRtrDocFile(index, event) {
      await this.takeRtrDocFile(index, event.dataTransfer?.files?.[0])
    },

    async takeRtrDocFile(index, file) {
      if (!file) return
      const doc = this.getDocUploadState('right_to_rent').rtrDocs[index]
      if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl)
      Object.assign(doc, this.blankRtrDoc(), { type: doc.type })
      doc.file = file
      doc.previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      await this.analyzeRtrDoc(doc)
    },

    // Claude reads the document: what it is, who it names, the date on it,
    // and whether it passes the Home Office rule for its kind. The reading
    // fills the slot in; the agent stays free to correct it.
    async analyzeRtrDoc(doc) {
      doc.state = 'analyzing'
      doc.error = null

      const formData = new FormData()
      formData.append('document', doc.file)
      formData.append('check_type', 'right_to_rent_group_2')

      try {
        const response = await fetch(`/people/${this.personId}/tenant-checks/analyze_document`, {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        const result = await response.json()
        if (!result.success) throw new Error(result.error || 'The document could not be read')

        doc.blobSignedId = result.blob_signed_id
        doc.analysis = result.analysis
        const known = this.rtrGroup2Options().some(o => o.value === result.analysis?.document_type)
        if (known) doc.type = result.analysis.document_type
        if (result.analysis?.document_date && this.rtrDocNeedsDate(doc.type)) doc.date = result.analysis.document_date
        doc.state = 'analyzed'
      } catch (error) {
        console.error('right_to_rent group 2 analysis error:', error)
        // The reading is help, not a gate: the agent has the original in
        // hand and can still name and date it themselves.
        doc.error = error.message || 'The document could not be read'
        doc.state = 'error'
      }
    },

    clearRtrDocFile(index) {
      const doc = this.getDocUploadState('right_to_rent').rtrDocs[index]
      if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl)
      Object.assign(doc, this.blankRtrDoc(), { type: doc.type })
    },

    // The points Claude checked on a slot's document, for the checklist
    // under it. Only the ones that apply to that kind of document.
    rtrDocChecklist(doc) {
      const a = doc.analysis
      if (!a) return []
      const items = []
      const push = (ok, label, note) => items.push({ ok, label, note })

      push(a.quality?.readable === true, 'Document readable', a.quality?.issues?.length ? a.quality.issues.join(', ') : null)
      const option = this.rtrGroup2Options().find(o => o.value === a.document_type)
      push(!!option, option ? `Recognised as ${option.label.toLowerCase()}` : 'Not a List A Group 2 document', a.document_description)
      if (a.document_type === 'birth_certificate') {
        push(a.names_parent === true, 'Full certificate naming a parent', a.names_parent === false ? 'A short certificate is not accepted' : null)
      }
      if (this.rtrDocNeedsDate(a.document_type)) {
        const within = this.rtrDocDateWithinThreeMonths(a.document_date)
        push(within === true, 'Dated within the last three months', a.document_date ? this.formatRtrDate(a.document_date) : 'No date found')
      }
      if (a.document_type === 'driving_licence') {
        const current = !a.expiry_date || new Date(a.expiry_date) >= new Date()
        push(current, 'Licence current', a.expiry_date ? `until ${this.formatRtrDate(a.expiry_date)}` : null)
      }
      if (a.name_matches !== null && a.name_matches !== undefined) {
        push(a.name_matches === true, 'Name matches applicant', a.name_matches ? a.holder_name : a.match_notes)
      }
      if (a.dob_matches !== null && a.dob_matches !== undefined) {
        push(a.dob_matches === true, 'Date of birth matches', a.dob_matches ? null : a.match_notes)
      }
      return items
    },

    rtrDocDateWithinThreeMonths(iso) {
      if (!iso) return null
      const checkDate = new Date(this.getDocUploadState('right_to_rent').rtrCheckDate || Date.now())
      const floor = new Date(checkDate)
      floor.setMonth(floor.getMonth() - 3)
      return new Date(iso) >= floor
    },

    formatRtrDate(iso) {
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    canRecordRtrDocuments() {
      const upload = this.getDocUploadState('right_to_rent')
      if (upload.saving || !upload.rtrCheckDate) return false
      return upload.rtrDocs.every(d =>
        d.file && d.state !== 'analyzing' && d.type && (!this.rtrDocNeedsDate(d.type) || d.date)
      )
    },

    async recordRtrDocuments() {
      const upload = this.getDocUploadState('right_to_rent')
      if (!this.canRecordRtrDocuments()) return

      upload.saving = true
      upload.error = null

      // A document Claude has read is already stored as a blob; one it could
      // not read goes up as the file itself.
      const formData = new FormData()
      upload.rtrDocs.forEach(d => {
        formData.append('documents[]', d.blobSignedId || d.file)
        formData.append('document_types[]', d.type)
        formData.append('document_dates[]', this.rtrDocNeedsDate(d.type) ? d.date : '')
        formData.append('document_analyses[]', d.analysis ? JSON.stringify(d.analysis) : '')
      })
      formData.append('rtr_check_date', upload.rtrCheckDate)

      const tenancy = this.person?.current_tenancy
      const applicant = this.person?.current_tenancy_applicant
      if (this.complianceGuaranteeId) {
        formData.append('guarantee_id', this.complianceGuaranteeId)
      } else if (tenancy) {
        formData.append('tenancy_id', tenancy.id)
      } else if (applicant) {
        formData.append('tenancy_application_applicant_id', applicant.id)
      }

      try {
        const response = await fetch(`/people/${this.personId}/tenant-checks/right_to_rent_documents`, {
          method: 'POST',
          body: formData,
          headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content }
        })
        const result = await response.json()

        if (result.success) {
          await this.fetchPerson()
          this.closeRtrDocumentsForm()
          this.resetDocUpload('right_to_rent')
          this.getDocUploadState('right_to_rent').open = true
          if (result.referencing_ready && result.application) {
            this.handleReferencingReady(result.application)
          }
        } else {
          upload.error = result.error || 'The check could not be recorded.'
        }
      } catch (error) {
        console.error('right_to_rent documents error:', error)
        upload.error = 'The check could not be recorded. Please try again.'
      } finally {
        upload.saving = false
      }
    },

    // ========== Mismatch Detection ==========

    hasDocumentMismatch(checkType) {
      const upload = this.getDocUploadState(checkType)
      if (!upload.analysisResult) return { hasMismatch: false, mismatchType: null }

      const result = upload.analysisResult
      const nameMatch = result.name_matches !== false
      const dobMatch = result.dob_matches !== false

      if (!nameMatch && !dobMatch) return { hasMismatch: true, mismatchType: 'both' }
      if (!nameMatch) return { hasMismatch: true, mismatchType: 'name' }
      if (!dobMatch) return { hasMismatch: true, mismatchType: 'dob' }

      if (checkType === 'id_verification') {
        const validIdTypes = ['passport', 'driving_licence', 'national_id', 'brp', 'residence_permit']
        if (result.document_type && !validIdTypes.includes(result.document_type)) {
          return { hasMismatch: true, mismatchType: 'wrong_type' }
        }
      }

      if (result.quality && !result.quality.readable && !result.holder_name && !result.name_on_document) {
        return { hasMismatch: true, mismatchType: 'unreadable' }
      }

      return { hasMismatch: false, mismatchType: null }
    },

    getMismatchDescription(mismatchType) {
      const descriptions = {
        'name': 'The name on this document does not match the applicant\'s name on file.',
        'dob': 'The date of birth on this document does not match the applicant\'s date of birth on file.',
        'both': 'Both the name and date of birth on this document do not match the applicant\'s details on file.',
        'wrong_type': 'This document type is not a valid ID document for verification.',
        'unreadable': 'This document could not be read properly. Key information could not be extracted.'
      }
      return descriptions[mismatchType] || 'There is an issue with this document that requires review.'
    },

    // ========== Override Modal ==========

    openOverrideModal(checkType) {
      const { hasMismatch, mismatchType } = this.hasDocumentMismatch(checkType)
      if (!hasMismatch) {
        this.saveVerification(checkType, 'accepted')
        return
      }

      this.overrideModal = {
        open: true,
        checkType: checkType,
        reason: '',
        mismatchType: mismatchType,
        saving: false,
        error: null
      }
    },

    closeOverrideModal() {
      this.overrideModal = {
        open: false,
        checkType: null,
        reason: '',
        mismatchType: null,
        saving: false,
        error: null
      }
    },

    async submitOverride() {
      if (!this.overrideModal.reason?.trim()) {
        this.overrideModal.error = 'Please enter a reason for accepting this document'
        return
      }

      this.overrideModal.saving = true
      this.overrideModal.error = null

      try {
        const checkType = this.overrideModal.checkType
        const upload = this.getDocUploadState(checkType)

        const tenancy = this.person?.current_tenancy
        const applicant = this.person?.current_tenancy_applicant

        // A guarantor's checks hang off the guarantee; they need no tenancy
        // or application of their own.
        if (!this.complianceGuaranteeId && !tenancy && !applicant) {
          throw new Error('No active tenancy or application found')
        }

        const checkData = {
          tenant_check: {
            check_type: checkType,
            status: 'accepted',
            documents: [upload.blobSignedId],
            override_reason: this.overrideModal.reason.trim(),
            override_mismatch_type: this.overrideModal.mismatchType
          }
        }

        if (this.checkTypeUsesAI(checkType) && upload.analysisResult) {
          checkData.tenant_check.verification_method = 'ai_assisted'
          checkData.tenant_check.ai_confidence = upload.analysisResult.confidence
          checkData.tenant_check.document_type = upload.analysisResult.document_type

          if (checkType === 'id_verification') {
            checkData.tenant_check.document_number = upload.analysisResult.document_number
            checkData.tenant_check.document_expiry_date = upload.analysisResult.expiry_date
            checkData.tenant_check.issuing_country = upload.analysisResult.issuing_country
            checkData.tenant_check.extracted_name = upload.analysisResult.holder_name
            checkData.tenant_check.extracted_dob = upload.analysisResult.date_of_birth
          }
        }

        // Why the check exists. A guarantor is neither a tenant nor an
        // applicant, so their checks hang off the guarantee — and it wins,
        // because a guarantor may well be somebody else's tenant too.
        if (this.complianceGuaranteeId) {
          checkData.tenant_check.guarantee_id = this.complianceGuaranteeId
        } else if (tenancy) {
          checkData.tenant_check.tenancy_id = tenancy.id
        } else {
          checkData.tenant_check.tenancy_application_applicant_id = applicant.id
        }

        if (upload.isPdf && upload.thumbnailUrl) {
          checkData.tenant_check.thumbnail = upload.thumbnailUrl
        }

        const response = await fetch(`/people/${this.personId}/tenant-checks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(checkData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save verification')
        }

        await this.fetchPerson()
        this.resetDocUpload(checkType)
        this.getDocUploadState(checkType).open = false
        this.closeOverrideModal()
      } catch (error) {
        console.error('Failed to submit override:', error)
        this.overrideModal.error = error.message || 'Failed to save. Please try again.'
      } finally {
        this.overrideModal.saving = false
      }
    },

    // ========== Referencing Modal ==========

    // Called when accepting the final compliance check makes referencing complete
    // (backend returns `referencing_ready`). Inside the application slideover we reveal
    // the inline "Approve Referencing" button in real time — no re-fetch, no loading
    // flash. Elsewhere (e.g. the person slideover) we fall back to the approval modal.
    handleReferencingReady(application) {
      if (!application) return

      if (this.application && this.application.id === application.id) {
        this.application.referencing_complete = true
        return
      }

      this.showReferencingApprovalModal(application.id, application.property_display_name)
    },

    showReferencingApprovalModal(applicationId, propertyName) {
      this.referencingModal = {
        open: true,
        applicationId: applicationId,
        propertyName: propertyName,
        saving: false,
        error: null
      }
    },

    closeReferencingModal() {
      this.referencingModal = {
        open: false,
        applicationId: null,
        propertyName: null,
        saving: false,
        error: null
      }
    },

    async approveReferencingFromModal() {
      if (!this.referencingModal.applicationId) return

      this.referencingModal.saving = true
      this.referencingModal.error = null

      try {
        const response = await fetch(`/tenancy-applications/${this.referencingModal.applicationId}/approve_referencing`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to approve referencing')
        }

        this.closeReferencingModal()
        await this.fetchPerson()
      } catch (error) {
        console.error('Failed to approve referencing:', error)
        this.referencingModal.error = error.message || 'Failed to approve referencing. Please try again.'
      } finally {
        this.referencingModal.saving = false
      }
    },

    // ========== Accept / Reject Verification ==========

    async acceptVerification(checkType) {
      const { hasMismatch } = this.hasDocumentMismatch(checkType)
      if (hasMismatch) {
        this.openOverrideModal(checkType)
        return
      }
      await this.saveVerification(checkType, 'accepted')
    },

    showRejectReasonInput(checkType) {
      const upload = this.getDocUploadState(checkType)
      upload.showRejectInput = true
      this.$nextTick(() => {
        const input = this.$el.querySelector(`[data-reject-input="${checkType}"]`)
        if (input) input.focus()
      })
    },

    cancelReject(checkType) {
      const upload = this.getDocUploadState(checkType)
      upload.showRejectInput = false
      upload.rejectReason = ''
    },

    async rejectVerification(checkType, reason = null) {
      const upload = this.getDocUploadState(checkType)
      const rejectReason = reason || upload.rejectReason
      if (!rejectReason?.trim()) return
      await this.saveVerification(checkType, 'rejected', rejectReason.trim())
    },

    async acceptExistingVerification(checkType) {
      await this.updateExistingVerification(checkType, 'accepted')
    },

    async rejectExistingVerification(checkType, reason = null) {
      const upload = this.getDocUploadState(checkType)
      const rejectReason = reason || upload.rejectReason
      if (!rejectReason?.trim()) return
      await this.updateExistingVerification(checkType, 'rejected', rejectReason.trim())
    },

    async updateExistingVerification(checkType, status, failureReason = null) {
      const upload = this.getDocUploadState(checkType)

      const existingCheck = this.person?.tenant_checks?.find(c => c.check_type === checkType)
      if (!existingCheck) {
        upload.error = 'No existing check found to update'
        return
      }

      try {
        const updateData = { tenant_check: { status: status } }

        if (failureReason) {
          updateData.tenant_check.failure_reason = failureReason
        }
        if (status === 'accepted') {
          updateData.tenant_check.completed_at = new Date().toISOString()
        }

        const response = await fetch(`/people/${this.personId}/tenant-checks/${existingCheck.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(updateData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to update verification')
        }

        const result = await response.json()
        await this.fetchPerson()
        this.resetDocUpload(checkType)

        if (status === 'rejected') {
          this.getDocUploadState(checkType).open = true
        } else {
          this.getDocUploadState(checkType).open = false
        }

        if (result.referencing_ready && result.application) {
          this.handleReferencingReady(result.application)
        }
      } catch (error) {
        console.error(`Failed to update ${checkType}:`, error)
        upload.error = error.message || 'Failed to update verification. Please try again.'
      }
    },

    async saveVerification(checkType, status, failureReason = null) {
      const upload = this.getDocUploadState(checkType)

      const tenancy = this.person?.current_tenancy
      const applicant = this.person?.current_tenancy_applicant

      // A guarantor's checks hang off the guarantee; they need no tenancy
      // or application of their own.
      if (!this.complianceGuaranteeId && !tenancy && !applicant) {
        upload.error = 'No active tenancy or application found for this person'
        upload.state = 'error'
        return
      }

      try {
        const checkData = {
          tenant_check: {
            check_type: checkType,
            status: status,
            documents: [upload.blobSignedId]
          }
        }

        if (this.checkTypeUsesAI(checkType) && upload.analysisResult) {
          checkData.tenant_check.verification_method = 'ai_assisted'
          checkData.tenant_check.ai_confidence = upload.analysisResult.confidence
          checkData.tenant_check.document_type = upload.analysisResult.document_type

          if (checkType === 'id_verification') {
            checkData.tenant_check.document_number = upload.analysisResult.document_number
            checkData.tenant_check.document_expiry_date = upload.analysisResult.expiry_date
            checkData.tenant_check.issuing_country = upload.analysisResult.issuing_country
            checkData.tenant_check.extracted_name = upload.analysisResult.holder_name
            checkData.tenant_check.extracted_dob = upload.analysisResult.date_of_birth
          } else if (checkType === 'address_verification') {
            checkData.tenant_check.document_date = upload.analysisResult.document_date
            checkData.tenant_check.extracted_name = upload.analysisResult.name_on_document
            checkData.tenant_check.ai_extraction_data = upload.analysisResult
          } else if (checkType === 'right_to_rent') {
            checkData.tenant_check.extracted_name = upload.analysisResult.name
            checkData.tenant_check.extracted_dob = upload.analysisResult.date_of_birth
            checkData.tenant_check.share_code = upload.analysisResult.reference_number
            checkData.tenant_check.rtr_check_date = upload.analysisResult.check_date
            if (upload.analysisResult.is_time_limited && upload.analysisResult.expiry_date) {
              checkData.tenant_check.expiry_date = upload.analysisResult.expiry_date
            }
            const statusToBasis = {
              'settled': 'share_code_unlimited',
              'indefinite_leave': 'share_code_unlimited',
              'pre_settled': 'share_code_time_limited',
              'limited_leave': 'share_code_time_limited'
            }
            checkData.tenant_check.rtr_basis = statusToBasis[upload.analysisResult.status] || 'share_code_time_limited'
            checkData.tenant_check.ai_extraction_data = upload.analysisResult
          }
        } else {
          checkData.tenant_check.verification_method = 'manual'
          if (upload.documentType) {
            checkData.tenant_check.document_type = upload.documentType
          }
        }

        // Why the check exists. A guarantor is neither a tenant nor an
        // applicant, so their checks hang off the guarantee — and it wins,
        // because a guarantor may well be somebody else's tenant too.
        if (this.complianceGuaranteeId) {
          checkData.tenant_check.guarantee_id = this.complianceGuaranteeId
        } else if (tenancy) {
          checkData.tenant_check.tenancy_id = tenancy.id
        } else {
          checkData.tenant_check.tenancy_application_applicant_id = applicant.id
        }

        if (failureReason) {
          checkData.tenant_check.failure_reason = failureReason
        }

        if (upload.isPdf && upload.thumbnailUrl) {
          checkData.tenant_check.thumbnail = upload.thumbnailUrl
        }

        const response = await fetch(`/people/${this.personId}/tenant-checks`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify(checkData)
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.errors?.[0] || 'Failed to save verification')
        }

        const result = await response.json()
        await this.fetchPerson()
        this.resetDocUpload(checkType)

        if (status === 'rejected') {
          this.getDocUploadState(checkType).open = true
        } else {
          this.getDocUploadState(checkType).open = false
        }

        if (result.referencing_ready && result.application) {
          this.handleReferencingReady(result.application)
        }
      } catch (error) {
        console.error(`Failed to save ${checkType}:`, error)
        upload.error = error.message || 'Failed to save verification. Please try again.'
        upload.state = 'error'
      }
    },

    // ========== Lightbox ==========

    openLightbox(imageUrl) {
      this.lightboxImageUrl = imageUrl
      this.lightboxOpen = true
    },

    closeLightbox() {
      this.lightboxOpen = false
      this.lightboxImageUrl = null
    }
  }
}
